"use client";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { MapPin } from "lucide-react";
import { useMap } from "react-leaflet";
import "@/styles/leaflet-custom.css";

// Dynamically import the map to avoid SSR issues
const MapContainer = dynamic(
  () => import("react-leaflet").then((mod) => mod.MapContainer),
  { ssr: false }
);

const TileLayer = dynamic(
  () => import("react-leaflet").then((mod) => mod.TileLayer),
  { ssr: false }
);

const Marker = dynamic(
  () => import("react-leaflet").then((mod) => mod.Marker),
  { ssr: false }
);

const Popup = dynamic(() => import("react-leaflet").then((mod) => mod.Popup), {
  ssr: false,
});

interface LocationMapProps {
  className?: string;
  companyName?: string;
  companyNameBn?: string;
}

const LocationMap: React.FC<LocationMapProps> = ({
  className = "",
  companyName = "Nippon Koei Bangladesh Ltd.",
  companyNameBn = "নিপ্পন কোয়েই বাংলাদেশ এলটিডি.",
}) => {
  const [isClient, setIsClient] = useState(false);
  const [L, setL] = useState<any>(null);
  const [mapLayer, setMapLayer] = useState<"street" | "satellite" | "hybrid">(
    "street"
  );

  // Nippon Koei BD office coordinates (Gulshan-2, Dhaka)
  const position: [number, number] = [23.793908046970053, 90.41225326459919]; // Exact coordinates for Gulshan-2

  useEffect(() => {
    setIsClient(true);
    // Import Leaflet only on client side
    import("leaflet").then((leaflet) => {
      setL(leaflet.default);
    });
  }, []);

  // Custom marker with company name using your SVG design
  const createCustomIcon = () => {
    if (!L) return null;

    return L.divIcon({
      className: "custom-marker-with-label",
      html: `
            <svg xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 48 72"
                 width="32" height="48"
                 role="img" aria-labelledby="title desc">
              <title id="title">Location marker</title>
              <desc id="desc">Red map pin with white center and subtle shadow</desc>

              <!-- subtle drop shadow -->
              <defs>
                <filter id="pinShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur"/>
                  <feOffset in="blur" dx="0" dy="4" result="offsetBlur"/>
                  <feFlood flood-color="#000" flood-opacity="0.24"/>
                  <feComposite in2="offsetBlur" operator="in"/>
                  <feMerge>
                    <feMergeNode/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              <!-- pin shape -->
              <g filter="url(#pinShadow)">
                <path
                  d="M24 2
                     C15.163 2 8.5 8.663 8.5 17.5
                     C8.5 29.75 24 48 24 48
                     C24 48 39.5 29.75 39.5 17.5
                     C39.5 8.663 32.837 2 24 2 Z"
                  fill="#EA4335"/>

                <!-- glossy highlight (optional) -->
                <path
                  d="M20 8
                     C17 10 16 13 16 17
                     C16 22 20 28 24 34
                     C24 34 29 25 29 17
                     C29 13 27.5 10 24 8
                     C22 7 21 7 20 8 Z"
                  fill="#ff6b61" opacity="0.18"/>

                <!-- inner white circle -->
                <circle cx="24" cy="17.5" r="6.5" fill="#fff"/>
                <circle cx="24" cy="17.5" r="3.2" fill="#EA4335"/>
              </g>
            </svg>
      `,
      iconSize: [20, 30],
      iconAnchor: [10, 30],
      popupAnchor: [6, -30],
    });
  };

  const MapEvents = () => {
    const map = useMap();
    useEffect(() => {
      if (map && L) {
        // Delay invalidateSize to ensure the map container has resized
        setTimeout(() => {
          map.invalidateSize();
          const customIcon = createCustomIcon();
          if (customIcon) {
            const marker = L.marker(position, { icon: customIcon }).addTo(map);

            const popupContent = `
            <div>
              <div style="font-size: 13px; font-weight: 600; color: #1A1D21; line-height: 1.3; margin-bottom: 3px; letter-spacing: -0.01em;">
                ${companyName}
              </div>
              <div style="font-size: 12px; font-weight: 500; color: #4F5662; line-height: 1.4; font-family: 'Noto Sans Bengali', 'Kalpurush', 'SiyamRupali', system-ui, sans-serif;">
                ${companyNameBn}
              </div>
            </div>
          `;

            marker.bindPopup(popupContent, {
              className: "custom-popup",
              maxWidth: 240,
            });

            // Open popup on initial load
            marker.openPopup();
          }
        }, 500);
      }
    }, [map, L]);
    return null;
  };

  if (!isClient) {
    // Loading placeholder
    return (
      <div
        className={`relative bg-gray-200 rounded-lg overflow-hidden ${className}`}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-500">
            <MapPin className="w-6 h-6 animate-pulse" />
            <span className="text-lg font-medium">Loading map...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Import Leaflet CSS */}
      <link
        rel="stylesheet"
        href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
        integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
        crossOrigin=""
      />

      {/* Import Google Fonts for Bengali text */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+Bengali:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      <div
        className={`relative rounded-lg overflow-hidden shadow-lg ${className}`}
      >
        {/* Map Layer Switcher */}
        <div className="absolute top-4 right-4 z-[1000] flex bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setMapLayer("street")}
            className={`px-3 py-2 text-xs font-medium transition-colors border-r border-gray-200 last:border-r-0 flex items-center gap-1 ${
              mapLayer === "street"
                ? "bg-[#1A5852] text-white"
                : "bg-white text-[#3D434C] hover:bg-gray-50"
            }`}
            title="Street Map View"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L2 7V17L12 22L22 17V7L12 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 22V12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 7L12 12L22 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Map
          </button>
          <button
            onClick={() => setMapLayer("satellite")}
            className={`px-3 py-2 text-xs font-medium transition-colors border-r border-gray-200 last:border-r-0 flex items-center gap-1 ${
              mapLayer === "satellite"
                ? "bg-[#1A5852] text-white"
                : "bg-white text-[#3D434C] hover:bg-gray-50"
            }`}
            title="Satellite View"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M2 12h20" stroke="currentColor" strokeWidth="2" />
              <path
                d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            Satellite
          </button>
          <button
            onClick={() => setMapLayer("hybrid")}
            className={`px-3 py-2 text-xs font-medium transition-colors flex items-center gap-1 ${
              mapLayer === "hybrid"
                ? "bg-[#1A5852] text-white"
                : "bg-white text-[#3D434C] hover:bg-gray-50"
            }`}
            title="Hybrid View"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="3"
                y="3"
                width="7"
                height="7"
                stroke="currentColor"
                strokeWidth="2"
                fill="currentColor"
                opacity="0.3"
              />
              <rect
                x="14"
                y="3"
                width="7"
                height="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <rect
                x="3"
                y="14"
                width="7"
                height="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <rect
                x="14"
                y="14"
                width="7"
                height="7"
                stroke="currentColor"
                strokeWidth="2"
                fill="currentColor"
                opacity="0.3"
              />
            </svg>
            Hybrid
          </button>
        </div>

        <MapContainer
          center={position}
          zoom={17}
          style={{ height: "100%", width: "100%" }}
          className="rounded-lg"
          scrollWheelZoom={true}
          zoomControl={true}
        >
          {/* Conditional Tile Layers */}
          {mapLayer === "street" && (
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          )}

          {mapLayer === "satellite" && (
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
          )}

          {mapLayer === "hybrid" && (
            <>
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                opacity={0.3}
              />
            </>
          )}
          <MapEvents />
        </MapContainer>
      </div>
    </>
  );
};

export default LocationMap;
